import axios from 'axios';
import React, {
  useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { auth } from '../../../Auth/Auth';
import { Audio, AudioContextType, AudioType } from '../../../../dorian-shared/types/audio/audio';
import {
  AudioSelectOption,
  AudioStepAction,
  MusicActionSettings,
  SFXActionSettings,
} from '../../../../dorian-shared/types/audio/AudioStepSettingsType';
import { api } from '../../../api';
import { Pagination } from '../../../ui/Pagination/Pagination';
import { AudiosContext } from '../../AudioLibraryPage/AudioLibraryContainer/AudiosProvider/AudiosProvider';
import notSharedIcon from '../../../../assets/images/eye-black.svg';
import sharedIcon from '../../../../assets/images/eye-green.svg';
import { actionMusicOptions, actionSFXSelectOptions } from '../utils';
import classes from './AudioTrackPicker.module.scss';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const DEBUG_AUDIO_PICKER_KEEP_OPEN = false;

const SORT_OPTIONS = [
  { value: 'group', label: 'Default' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'titleDesc', label: 'Title Z–A' },
  { value: 'createdAt', label: 'Date (newest)' },
  { value: 'createdAtAsc', label: 'Date (oldest)' },
] as const;

type AudioSort = typeof SORT_OPTIONS[number]['value'];

type AudioUsage = {
  storyId?: number | null;
  title?: string;
  chapter?: number | null;
  isBonus?: boolean;
};

function formatUnshareError(usage: AudioUsage | null, currentStoryId?: number) {
  if (usage?.storyId != null && currentStoryId != null
    && String(usage.storyId) === String(currentStoryId)) {
    return 'Audio file is used in this episode.';
  }
  const title = usage?.title?.trim();
  if (usage?.chapter != null && !Number.isNaN(Number(usage.chapter))) {
    const episode = title
      ? `Ep #${usage.chapter}: ${title}`
      : `Ep #${usage.chapter}`;
    return `Audio file is used in episode ${episode}.`;
  }
  if (title) {
    return `Audio file is used in episode ${title}.`;
  }
  return 'Audio file is used in this book.';
}

type AudioTrackPickerProps = {
  value: MusicActionSettings | SFXActionSettings;
  onChange: (value: MusicActionSettings | SFXActionSettings) => void;
  audioType: AudioType;
  disabled?: boolean;
  panelPlacement?: 'up' | 'down';
};

function SortGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4 2.5v11M4 2.5L2 4.5M4 2.5l2 2M12 13.5v-11M12 13.5l2-2M12 13.5l-2-2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatDuration(seconds?: number) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '';
  const total = Math.round(Number(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function ShareGlyph({
  audio, onUnshare, unshareBusy,
}: {
  audio: Audio;
  onUnshare: (audio: Audio) => void;
  unshareBusy: boolean;
}) {
  const user = auth.getUser();
  const context = useContext<AudioContextType>(AudiosContext);
  const updateAudio = context?.updateAudio;
  const upsertAudio = context?.upsertAudio;
  const patchAudio = context?.patchAudio;
  const [hasBookAccess, setHasBookAccess] = useState(!!audio.hasBookAccess);
  const iconRef = useRef<HTMLSpanElement>(null);
  const [tipPos, setTipPos] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setHasBookAccess(!!audio.hasBookAccess);
  }, [audio.hasBookAccess, audio.id]);

  const canManage = audio.userId === user?.id || user?.role === 'admin';

  const handleShare = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!updateAudio) return;
    try {
      const result = await updateAudio(audio.id, { action: 'share' });
      if (result) {
        setHasBookAccess(true);
        const next = { ...audio, hasBookAccess: true };
        patchAudio?.(audio.id, { hasBookAccess: true });
        upsertAudio?.(next);
      }
    } catch (error) {
      console.error('Error sharing audio:', error);
    }
  };

  const handleUnshare = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (unshareBusy) return;
    setTipPos(null);
    onUnshare(audio);
  };

  const showTip = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setTipPos({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  };

  const hideTip = () => {
    hideTimerRef.current = window.setTimeout(() => setTipPos(null), 150);
  };

  useEffect(() => () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
    }
  }, []);

  if (!audio.userName) return null;

  const tooltip = tipPos && createPortal(
    <div
      data-audio-share-tooltip="true"
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      style={{
        position: 'fixed',
        top: tipPos.top < 72 ? tipPos.top + tipPos.height : tipPos.top,
        left: tipPos.left + tipPos.width,
        transform: tipPos.top < 72
          ? 'translate(-100%, 4px)'
          : 'translate(-100%, -100%)',
        zIndex: 100000,
        display: 'flex',
        alignItems: 'stretch',
        pointerEvents: 'auto',
      }}
    >
      {hasBookAccess === false && canManage && (
        <Button
          size="sm"
          style={{
            flexShrink: 0,
            marginRight: 0,
            backgroundColor: '#4de8c3',
            borderColor: '#4de8c3',
            color: 'black',
          }}
          onClick={handleShare}
        >
          Share
        </Button>
      )}
      {hasBookAccess === true && canManage && (
        <Button
          size="sm"
          disabled={unshareBusy}
          style={{
            flexShrink: 0,
            marginRight: 0,
            backgroundColor: '#E8584D',
            borderColor: '#E8584D',
            color: 'black',
          }}
          onClick={handleUnshare}
        >
          Unshare
        </Button>
      )}
      <div
        style={{
          minWidth: '7em',
          padding: '0.25rem 0.5rem',
          color: '#fff',
          textAlign: 'center',
          background: '#000',
          borderRadius: '0.25rem',
          fontSize: '0.8rem',
          lineHeight: 1.3,
        }}
      >
        {hasBookAccess ? 'Shared by' : 'Owned by'}
        <br />
        {audio.userName}
      </div>
    </div>,
    document.body,
  );

  return (
    <span
      ref={iconRef}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      style={{
        display: 'inline-block',
        marginLeft: 8,
        lineHeight: 1,
        verticalAlign: 'middle',
      }}
    >
      <img
        src={hasBookAccess ? sharedIcon : notSharedIcon}
        alt=""
        style={{ width: '1.4em', height: 'auto' }}
      />
      {tooltip}
    </span>
  );
}

function TrackRow({
  audio, selected, onSelect, onUnshare, unshareBusy,
}: {
  audio: Audio;
  selected: boolean;
  onSelect: (audio: Audio) => void;
  onUnshare: (audio: Audio) => void;
  unshareBusy: boolean;
}) {
  return (
    <div className={`${classes.item} ${selected ? classes.itemSelected : ''}`}>
      <button
        type="button"
        className={classes.itemMain}
        onClick={() => onSelect(audio)}
      >
        <span className={classes.itemTitle}>{audio.title}</span>
        <span className={classes.itemMeta}>{formatDuration(audio.duration)}</span>
      </button>
      <div className={classes.share}>
        <ShareGlyph
          audio={audio}
          onUnshare={onUnshare}
          unshareBusy={unshareBusy}
        />
      </div>
    </div>
  );
}

export function AudioTrackPicker(props: AudioTrackPickerProps) {
  const {
    value, onChange, audioType, disabled, panelPlacement = 'down',
  } = props;

  const user = auth.getUser();
  const isAdmin = user?.role === 'admin';
  const context = useContext<AudioContextType>(AudiosContext);
  const {
    audios = [], bookId, storyId, upsertAudio, patchAudio, rememberAudioBaseUrl,
  } = context ?? {};
  const unshareLockRef = useRef(false);
  const [unshareBusy, setUnshareBusy] = useState(false);
  const [unshareError, setUnshareError] = useState<AudioUsage | null>(null);

  const actionOptions = audioType === AudioType.Music ? actionMusicOptions : actionSFXSelectOptions;
  const isPlay = value.action === AudioStepAction.Play;
  const cachedAudio = audios.find((audio) => String(audio.id) === String(value.audioId));
  const [pinnedAudio, setPinnedAudio] = useState<Audio | null>(null);
  const currentAudio = (
    pinnedAudio && String(pinnedAudio.id) === String(value.audioId)
      ? pinnedAudio
      : cachedAudio
  ) ?? null;

  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [groups, setGroups] = useState(() => (
    isAdmin ? ['applied', 'shared', 'owned', 'global'] : ['applied', 'shared', 'owned']
  ));
  const [sort, setSort] = useState<AudioSort>('group');
  const [results, setResults] = useState<Audio[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, groups, sort]);

  useEffect(() => {
    if (cachedAudio) {
      setPinnedAudio(cachedAudio);
    }
  }, [cachedAudio]);

  useEffect(() => {
    if (!open || DEBUG_AUDIO_PICKER_KEEP_OPEN) return undefined;
    const onDocClick = (event: MouseEvent) => {
      if (unshareBusy) {
        return;
      }
      const target = event.target as Node | null;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      if (target instanceof Element && (
        target.closest('[data-audio-share-tooltip]')
        || target.closest('[data-audio-sort-menu]')
        || target.closest('[data-audio-unshare-modal]')
      )) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick, true);
    return () => document.removeEventListener('mousedown', onDocClick, true);
  }, [open, unshareBusy]);

  useLayoutEffect(() => {
    if (!open || !isPlay) return undefined;
    const positionPanel = () => {
      const wrap = wrapRef.current;
      const panel = panelRef.current;
      if (!wrap || !panel) return;
      const modal = wrap.closest('.modal-content');
      const wrapRect = wrap.getBoundingClientRect();
      const modalRect = modal?.getBoundingClientRect();
      const width = (modalRect?.width ?? wrapRect.width) * 0.9;
      const anchorLeft = modalRect?.left ?? wrapRect.left;
      const anchorWidth = modalRect?.width ?? wrapRect.width;
      const left = anchorLeft + (anchorWidth - width) / 2 - wrapRect.left;
      panel.style.width = `${width}px`;
      panel.style.left = `${left}px`;
      panel.style.right = 'auto';
    };
    positionPanel();
    window.addEventListener('resize', positionPanel);
    return () => window.removeEventListener('resize', positionPanel);
  }, [open, isPlay, panelPlacement]);

  const fetchPage = useCallback(() => {
    if (!open) {
      return undefined;
    }
    if (!bookId || !storyId || groups.length === 0) {
      setResults([]);
      setTotalCount(0);
      return undefined;
    }
    const cancelToken = axios.CancelToken.source();
    setLoading(true);
    api.get(`/v1/audio/book/${bookId}`, {
      params: {
        storyId,
        type: audioType,
        search: debouncedSearch || undefined,
        groups: groups.join(','),
        sort,
        limit: PAGE_SIZE,
        offset: PAGE_SIZE * (page - 1),
        skipCount: page > 1 ? 1 : undefined,
      },
      cancelToken: cancelToken.token,
    }).then((res) => {
      setResults(res.data.audios || []);
      if (res.data.totalCount != null) {
        setTotalCount(Number(res.data.totalCount) || 0);
      }
      rememberAudioBaseUrl?.(res.data.audioBaseUrl);
    }).catch((error) => {
      if (!axios.isCancel(error)) {
        console.error('Error loading audio catalog:', error);
      }
    }).finally(() => setLoading(false));
    return () => cancelToken.cancel('Audio catalog unmount');
  }, [open, bookId, storyId, audioType, debouncedSearch, groups, sort, page, rememberAudioBaseUrl]);

  useEffect(() => fetchPage(), [fetchPage]);

  const currentAction = useMemo(() => (
    actionOptions.find((option) => option.action === value.action) ?? actionOptions[0]
  ), [actionOptions, value.action]);

  const handleActionChange = (selected: AudioSelectOption | null) => {
    const action = selected?.action ?? AudioStepAction.None;
    if (action !== AudioStepAction.Play) {
      onChange({
        ...value,
        action,
        audioId: null,
        audioUrl: '',
      });
      setOpen(false);
      return;
    }
    onChange({
      ...value,
      action: AudioStepAction.Play,
    });
  };

  const handleUnshareTrack = async (audio: Audio) => {
    if (unshareLockRef.current || unshareBusy) return;
    if (!bookId) return;
    unshareLockRef.current = true;
    setUnshareBusy(true);
    try {
      const res = await api.post(`/v1/audio/book/${bookId}/unshare`, {
        audioId: audio.id,
        storyId,
      });
      if (res.data?.success) {
        setResults((prev) => prev.map((item) => (
          String(item.id) === String(audio.id) ? { ...item, hasBookAccess: false } : item
        )));
        patchAudio?.(audio.id, { hasBookAccess: false });
        upsertAudio?.({ ...audio, hasBookAccess: false });
      }
    } catch (error) {
      const data = axios.isAxiosError(error) ? error.response?.data as {
        error?: string;
        usage?: AudioUsage;
      } | undefined : undefined;
      if (axios.isAxiosError(error) && error.response?.status === 409 && data?.error === 'used_in_book') {
        setUnshareError(data.usage || {});
      } else {
        console.error('Error unsharing audio:', error);
      }
    } finally {
      unshareLockRef.current = false;
      setUnshareBusy(false);
    }
  };

  const handleSelectTrack = (audio: Audio) => {
    setPinnedAudio(audio);
    upsertAudio?.(audio);
    onChange({
      ...value,
      action: AudioStepAction.Play,
      audioId: audio.id as MusicActionSettings['audioId'],
      audioUrl: audio.audioUrl,
    });
    setOpen(false);
  };

  const toggleGroup = (group: string) => {
    setGroups((prev) => (
      prev.includes(group) ? prev.filter((item) => item !== group) : [...prev, group]
    ));
  };

  const trackLabel = currentAudio?.title
    || (isPlay ? 'Choose a track' : 'Select Play to choose a track');

  return (
    <div className={classes.row}>
      <div className={classes.actionSelect}>
        <Select
          options={actionOptions}
          value={currentAction}
          onChange={handleActionChange}
          isDisabled={disabled}
          isSearchable={false}
        />
      </div>
      <div className={classes.trackWrap} ref={wrapRef}>
        <Button
          variant="outline-secondary"
          size="sm"
          className={`${classes.trackButton}${currentAudio ? '' : ` ${classes.trackButtonPlaceholder}`}`}
          disabled={disabled || !isPlay}
          onClick={() => {
            if (unshareBusy) return;
            setOpen((prev) => !prev);
          }}
        >
          {trackLabel}
        </Button>
        {open && isPlay && (
          <div
            ref={panelRef}
            className={`${classes.panel} ${panelPlacement === 'up' ? classes.panelUp : ''}`}
          >
            <div className={classes.search}>
              <Form.Control
                className={classes.searchInput}
                size="sm"
                placeholder="Search audio"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
            <div className={classes.filters}>
              <div className={classes.filterGroups}>
                <Form.Check
                  custom
                  type="checkbox"
                  id={`audio-group-applied-${audioType}`}
                  label="Used"
                  checked={groups.includes('applied')}
                  onChange={() => toggleGroup('applied')}
                />
                <Form.Check
                  custom
                  type="checkbox"
                  id={`audio-group-shared-${audioType}`}
                  label="Shared"
                  checked={groups.includes('shared')}
                  onChange={() => toggleGroup('shared')}
                />
                <Form.Check
                  custom
                  type="checkbox"
                  id={`audio-group-owned-${audioType}`}
                  label="Owned"
                  checked={groups.includes('owned')}
                  onChange={() => toggleGroup('owned')}
                />
                {isAdmin && (
                  <Form.Check
                    custom
                    type="checkbox"
                    id={`audio-group-global-${audioType}`}
                    label="All"
                    checked={groups.includes('global')}
                    onChange={() => toggleGroup('global')}
                  />
                )}
              </div>
              <Dropdown alignRight className={classes.sort}>
                <Dropdown.Toggle
                  variant="link"
                  size="sm"
                  className={classes.sortToggle}
                  aria-label="Sort audio"
                  title={`Sort: ${SORT_OPTIONS.find((option) => option.value === sort)?.label}`}
                >
                  <SortGlyph />
                </Dropdown.Toggle>
                <Dropdown.Menu data-audio-sort-menu="true">
                  {SORT_OPTIONS.map((option) => (
                    <Dropdown.Item
                      key={option.value}
                      active={sort === option.value}
                      onClick={() => setSort(option.value)}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className={classes.list}>
              {loading && results.length === 0 && (
                <div className={classes.loading}>
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {!loading && groups.length === 0 && (
                <div className={classes.empty}>Select a group to browse audio.</div>
              )}
              {!loading && groups.length > 0 && results.length === 0 && (
                <div className={classes.empty}>No audio matches.</div>
              )}
              {results.map((audio) => (
                <TrackRow
                  key={audio.id}
                  audio={audio}
                  selected={String(audio.id) === String(value.audioId)}
                  onSelect={handleSelectTrack}
                  onUnshare={handleUnshareTrack}
                  unshareBusy={unshareBusy}
                />
              ))}
            </div>
            <div className={classes.pager}>
              <Pagination
                className="pagination-bar mb-0"
                currentPage={page}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                nearbyCount={1}
              />
            </div>
            {unshareBusy && (
              <div className={classes.busyOverlay} aria-live="polite">
                <Spinner animation="border" size="sm" />
                <span>Unsharing…</span>
              </div>
            )}
          </div>
        )}
      </div>
      {unshareError && createPortal(
        <div
          data-audio-unshare-modal="true"
          className={classes.unshareOverlay}
        >
          <div
            className={classes.unshareDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="audio-unshare-error-title"
          >
            <h6 id="audio-unshare-error-title">Cannot unshare</h6>
            <p>{formatUnshareError(unshareError, storyId)}</p>
            <div className={classes.unshareActions}>
              <Button
                variant="secondary"
                size="sm"
                className={classes.unshareOk}
                onClick={() => setUnshareError(null)}
              >
                OK
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
